/**
 * Authentication context
 */
import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { User, UserRole } from '../types/user';
import { Employee, LocationType } from '../types/employee';

interface AuthContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasRole: (roles: string[]) => boolean;
  // Role checks
  isAdmin: () => boolean;
  isHR: () => boolean;
  isOnshoreHR: () => boolean;
  isOffshoreHR: () => boolean;
  isITSupport: () => boolean;
  // Permission checks
  canDelete: () => boolean;
  canEdit: () => boolean;
  canCreate: () => boolean;
  canEditEmployee: (employee: Employee) => boolean;
  canAccessEmployee: (employee: Employee) => boolean;
  canEditITFields: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for stored user on mount
    const storedUser = authService.getStoredUser();
    if (storedUser) {
      setUser(storedUser);
    }
    setIsLoading(false);
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await authService.login({ username, password });
      setUser(response.user);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const hasRole = (roles: string[]): boolean => {
    return user ? roles.includes(user.role) : false;
  };

  // Role checks
  const isAdmin = (): boolean => {
    return user?.role === UserRole.ADMIN;
  };

  const isHR = (): boolean => {
    return user ? [UserRole.ADMIN, UserRole.ONSHORE_HR, UserRole.OFFSHORE_HR].includes(user.role as UserRole) : false;
  };

  const isOnshoreHR = (): boolean => {
    return user?.role === UserRole.ONSHORE_HR;
  };

  const isOffshoreHR = (): boolean => {
    return user?.role === UserRole.OFFSHORE_HR;
  };

  const isITSupport = (): boolean => {
    return user ? [UserRole.IT_SUPPORT, UserRole.ONSHORE_IT, UserRole.OFFSHORE_IT].includes(user.role as UserRole) : false;
  };

  // Permission checks
  const canDelete = (): boolean => {
    return user?.role === UserRole.ADMIN;
  };

  const canEdit = (): boolean => {
    return user ? [UserRole.ADMIN, UserRole.ONSHORE_HR, UserRole.OFFSHORE_HR].includes(user.role as UserRole) : false;
  };

  const canCreate = (): boolean => {
    return user ? [UserRole.ADMIN, UserRole.ONSHORE_HR, UserRole.OFFSHORE_HR].includes(user.role as UserRole) : false;
  };

  const canEditEmployee = (employee: Employee): boolean => {
    if (!user) return false;
    if (user.role === UserRole.ADMIN) return true;
    if (user.role === UserRole.ONSHORE_HR) {
      return employee.location_type === LocationType.ONSHORE;
    }
    if (user.role === UserRole.OFFSHORE_HR) {
      return employee.location_type === LocationType.OFFSHORE;
    }
    // All IT Support roles have read-only access
    if ([UserRole.IT_SUPPORT, UserRole.ONSHORE_IT, UserRole.OFFSHORE_IT].includes(user.role as UserRole)) {
      return false;
    }
    return false;
  };

  const canAccessEmployee = (employee: Employee): boolean => {
    if (!user) return false;
    if (user.role === UserRole.ADMIN) return true;
    // All IT Support roles have read-only access to all employees
    if ([UserRole.IT_SUPPORT, UserRole.ONSHORE_IT, UserRole.OFFSHORE_IT].includes(user.role as UserRole)) {
      return true;
    }
    if (user.role === UserRole.ONSHORE_HR) {
      return employee.location_type === LocationType.ONSHORE;
    }
    if (user.role === UserRole.OFFSHORE_HR) {
      return employee.location_type === LocationType.OFFSHORE;
    }
    return false;
  };

  const canEditITFields = (): boolean => {
    return user ? [UserRole.ADMIN, UserRole.IT_SUPPORT, UserRole.ONSHORE_IT, UserRole.OFFSHORE_IT].includes(user.role as UserRole) : false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        login,
        logout,
        isAuthenticated: !!user,
        isLoading,
        hasRole,
        // Role checks
        isAdmin,
        isHR,
        isOnshoreHR,
        isOffshoreHR,
        isITSupport,
        // Permission checks
        canDelete,
        canEdit,
        canCreate,
        canEditEmployee,
        canAccessEmployee,
        canEditITFields,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
